import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { usePermissions } from '../hooks/usePermissions';
import { Permission } from '../types';
import { CircularProgress, Box, Typography, Alert } from '@mui/material';
import { useAuth } from '../hooks/useAuth';
import { useOrganization } from '../contexts/OrganizationContext';
import { getRoutePermission } from '../utils/routePermissions';
import { getPermissionLabel } from '../utils/permissions';

interface PermissionProtectedRouteProps {
  permission?: Permission; // Optional: if no permission is specified, checks route permission or just authentication
  children: React.ReactNode;
}

export const PermissionProtectedRoute = ({ permission, children }: PermissionProtectedRouteProps) => {
  const { hasPermission, loading } = usePermissions();
  const { user, loading: authLoading } = useAuth();
  const { currentOrganization, loading: orgLoading } = useOrganization();
  const location = useLocation();

  // Aguardar até que autenticação, organização e permissões estejam carregadas
  if (authLoading || orgLoading || loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  // Se não há organização ainda, aguardar (pode estar carregando)
  if (!currentOrganization) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  // Se não foi especificada uma permissão, tentar obter da rota
  const requiredPermission = permission || getRoutePermission(location.pathname);

  if (requiredPermission && !hasPermission(requiredPermission)) {
    // Mostrar mensagem de acesso negado ao invés de apenas redirecionar
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        padding={3}
      >
        <Alert severity="error" sx={{ maxWidth: 600, width: '100%' }}>
          <Typography variant="h6" gutterBottom>
            Acesso Negado
          </Typography>
          <Typography variant="body1">
            Você não tem permissão para acessar esta página.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Permissão necessária: <strong>{getPermissionLabel(requiredPermission)}</strong>
          </Typography>
          <Typography variant="body2" sx={{ mt: 2 }}>
            Entre em contato com o administrador da organização se você acredita que deveria ter acesso a esta funcionalidade.
          </Typography>
        </Alert>
      </Box>
    );
  }

  return <>{children}</>;
};
